import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader, SyncButton } from "@/components/admin/crud-helpers";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { format, subDays, startOfDay, endOfDay, eachDayOfInterval } from "date-fns";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Legend,
} from "recharts";
import { CalendarIcon, Download, FileText } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/branch-performance")({
  component: BranchPerformancePage,
});

type Branch = { id: string; name: string; code: string; commission_rate: number; type: string; status: string };
type Order = {
  id: string;
  branch_id: string | null;
  status: string;
  total_cents: number;
  currency: string;
  user_id: string | null;
  created_at: string;
  paid_at: string | null;
};

type RangePreset = "7d" | "30d" | "90d" | "ytd" | "custom";

function BranchPerformancePage() {
  const [branchId, setBranchId] = useState<string>("all");
  const [preset, setPreset] = useState<RangePreset>("30d");
  const [customFrom, setCustomFrom] = useState<Date | undefined>(subDays(new Date(), 30));
  const [customTo, setCustomTo] = useState<Date | undefined>(new Date());

  const { from, to } = useMemo(() => resolveRange(preset, customFrom, customTo), [preset, customFrom, customTo]);

  const branches = useQuery({
    queryKey: ["branches_perf"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("branches")
        .select("id, name, code, commission_rate, type, status")
        .order("name");
      if (error) throw error;
      return (data ?? []) as Branch[];
    },
  });

  const orders = useQuery({
    queryKey: ["orders_perf", from.toISOString(), to.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("id, branch_id, status, total_cents, currency, user_id, created_at, paid_at")
        .gte("created_at", from.toISOString())
        .lte("created_at", to.toISOString())
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as Order[];
    },
  });

  const branchMap = useMemo(() => new Map((branches.data ?? []).map(b => [b.id, b])), [branches.data]);

  const scoped = useMemo(() => {
    const rows = orders.data ?? [];
    if (branchId === "all") return rows;
    return rows.filter(o => o.branch_id === branchId);
  }, [orders.data, branchId]);

  const kpis = useMemo(() => {
    const paid = scoped.filter(o => o.status === "paid" || !!o.paid_at);
    const revenue = paid.reduce((s, o) => s + (o.total_cents ?? 0), 0);
    const refunded = scoped.filter(o => o.status === "refunded").reduce((s, o) => s + (o.total_cents ?? 0), 0);
    const orderCount = scoped.length;
    const paidCount = paid.length;
    const aov = paidCount ? Math.round(revenue / paidCount) : 0;
    const uniqueCustomers = new Set(paid.map(o => o.user_id).filter(Boolean)).size;
    const conv = orderCount ? (paidCount / orderCount) : 0;

    const branch = branchId === "all" ? null : branchMap.get(branchId) ?? null;
    const commission = branch ? Math.round(revenue * Number(branch.commission_rate ?? 0) / 100) : 0;

    return { revenue, refunded, orderCount, paidCount, aov, uniqueCustomers, conv, commission, branch };
  }, [scoped, branchId, branchMap]);

  const timeSeries = useMemo(() => {
    const days = eachDayOfInterval({ start: startOfDay(from), end: endOfDay(to) });
    const byDay = new Map<string, { date: string; revenue: number; orders: number }>();
    for (const d of days) {
      const key = format(d, "yyyy-MM-dd");
      byDay.set(key, { date: key, revenue: 0, orders: 0 });
    }
    for (const o of scoped) {
      const key = format(new Date(o.created_at), "yyyy-MM-dd");
      const row = byDay.get(key);
      if (!row) continue;
      row.orders += 1;
      if (o.status === "paid" || !!o.paid_at) row.revenue += (o.total_cents ?? 0) / 100;
    }
    return Array.from(byDay.values());
  }, [scoped, from, to]);

  const perBranchBars = useMemo(() => {
    const map = new Map<string, { name: string; revenue: number; orders: number }>();
    for (const b of branches.data ?? []) map.set(b.id, { name: b.code || b.name, revenue: 0, orders: 0 });
    for (const o of orders.data ?? []) {
      if (!o.branch_id) continue;
      const row = map.get(o.branch_id);
      if (!row) continue;
      row.orders += 1;
      if (o.status === "paid" || !!o.paid_at) row.revenue += (o.total_cents ?? 0) / 100;
    }
    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 10);
  }, [branches.data, orders.data]);

  const exportCsv = () => {
    const branchLabel = kpis.branch ? `${kpis.branch.name} (${kpis.branch.code})` : "All branches";
    const lines: string[] = [];
    lines.push(`Branch performance,${branchLabel}`);
    lines.push(`Range,${format(from, "yyyy-MM-dd")} to ${format(to, "yyyy-MM-dd")}`);
    lines.push("");
    lines.push("Metric,Value");
    lines.push(`Revenue (paid),${(kpis.revenue / 100).toFixed(2)}`);
    lines.push(`Refunded,${(kpis.refunded / 100).toFixed(2)}`);
    lines.push(`Orders,${kpis.orderCount}`);
    lines.push(`Paid orders,${kpis.paidCount}`);
    lines.push(`Avg order value,${(kpis.aov / 100).toFixed(2)}`);
    lines.push(`Unique customers,${kpis.uniqueCustomers}`);
    lines.push(`Conversion,${(kpis.conv * 100).toFixed(1)}%`);
    if (kpis.branch) lines.push(`Commission (${kpis.branch.commission_rate}%),${(kpis.commission / 100).toFixed(2)}`);
    lines.push("");
    lines.push("Date,Revenue,Orders");
    for (const r of timeSeries) lines.push(`${r.date},${r.revenue.toFixed(2)},${r.orders}`);
    if (branchId === "all") {
      lines.push("");
      lines.push("Top branches,Revenue,Orders");
      for (const r of perBranchBars) lines.push(`${r.name},${r.revenue.toFixed(2)},${r.orders}`);
    }
    downloadBlob(
      new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }),
      `branch-performance-${branchLabel.replace(/[^a-z0-9]+/gi, "_")}-${format(from, "yyyyMMdd")}-${format(to, "yyyyMMdd")}.csv`,
    );
  };

  const exportPdf = async () => {
    const { default: jsPDF } = await import("jspdf");
    const { default: autoTable } = await import("jspdf-autotable");
    const branchLabel = kpis.branch ? `${kpis.branch.name} (${kpis.branch.code})` : "All branches";
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Branch performance report", 14, 18);
    doc.setFontSize(10);
    doc.text(branchLabel, 14, 25);
    doc.text(`Range: ${format(from, "yyyy-MM-dd")} → ${format(to, "yyyy-MM-dd")}`, 14, 31);

    autoTable(doc, {
      startY: 38,
      head: [["Metric", "Value"]],
      body: [
        ["Revenue (paid)", fmtMoney(kpis.revenue)],
        ["Refunded", fmtMoney(kpis.refunded)],
        ["Orders", String(kpis.orderCount)],
        ["Paid orders", String(kpis.paidCount)],
        ["Avg order value", fmtMoney(kpis.aov)],
        ["Unique customers", String(kpis.uniqueCustomers)],
        ["Conversion", `${(kpis.conv * 100).toFixed(1)}%`],
        ...(kpis.branch ? [[`Commission (${kpis.branch.commission_rate}%)`, fmtMoney(kpis.commission)]] : []),
      ],
    });

    autoTable(doc, {
      head: [["Date", "Revenue", "Orders"]],
      body: timeSeries.map(r => [r.date, r.revenue.toFixed(2), String(r.orders)]),
    });

    if (branchId === "all") {
      autoTable(doc, {
        head: [["Branch", "Revenue", "Orders"]],
        body: perBranchBars.map(r => [r.name, r.revenue.toFixed(2), String(r.orders)]),
      });
    }

    doc.save(`branch-performance-${format(from, "yyyyMMdd")}-${format(to, "yyyyMMdd")}.pdf`);
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Branch performance"
        description="Charts and KPIs per branch with selectable date ranges."
        actions={
          <>
            <SyncButton onSync={() => { branches.refetch(); orders.refetch(); }} loading={branches.isFetching || orders.isFetching} />
            <Button variant="outline" size="sm" onClick={exportCsv}><Download className="mr-1 h-4 w-4" /> CSV</Button>
            <Button variant="outline" size="sm" onClick={exportPdf}><FileText className="mr-1 h-4 w-4" /> PDF</Button>
            <Button asChild variant="outline" size="sm"><Link to="/admin/branches">Branches</Link></Button>
          </>
        }
      />

      <div className="flex flex-wrap gap-2 items-end">
        <div className="grid gap-1">
          <span className="text-xs text-muted-foreground">Branch</span>
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger className="w-[260px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {(branches.data ?? []).map(b => <SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1">
          <span className="text-xs text-muted-foreground">Range</span>
          <Select value={preset} onValueChange={(v) => setPreset(v as RangePreset)}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="ytd">Year to date</SelectItem>
              <SelectItem value="custom">Custom…</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {preset === "custom" && (
          <>
            <DateField label="From" value={customFrom} onChange={setCustomFrom} />
            <DateField label="To" value={customTo} onChange={setCustomTo} />
          </>
        )}
        <div className="text-xs text-muted-foreground pb-2">
          {format(from, "PP")} → {format(to, "PP")}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Revenue" value={fmtMoney(kpis.revenue)} />
        <Kpi label="Orders" value={String(kpis.orderCount)} sub={`${kpis.paidCount} paid`} />
        <Kpi label="Avg order value" value={fmtMoney(kpis.aov)} />
        <Kpi label="Customers" value={String(kpis.uniqueCustomers)} />
        <Kpi label="Conversion" value={`${(kpis.conv * 100).toFixed(1)}%`} />
        <Kpi label="Refunded" value={fmtMoney(kpis.refunded)} tone={kpis.refunded > 0 ? "danger" : undefined} />
        {kpis.branch
          ? <Kpi label={`Commission (${kpis.branch.commission_rate}%)`} value={fmtMoney(kpis.commission)} />
          : <Kpi label="Branches" value={String((branches.data ?? []).length)} />}
        <Kpi label="Period days" value={String(timeSeries.length)} />
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-center mb-2">
            <div className="font-medium">Revenue & orders over time</div>
            {orders.isLoading && <span className="text-xs text-muted-foreground">Loading…</span>}
          </div>
          <div className="h-72 w-full">
            <ResponsiveContainer>
              <LineChart data={timeSeries} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis yAxisId="r" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis yAxisId="o" orientation="right" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }} />
                <Legend />
                <Line yAxisId="r" type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} name="Revenue" />
                <Line yAxisId="o" type="monotone" dataKey="orders" stroke="hsl(var(--muted-foreground))" strokeWidth={2} dot={false} name="Orders" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {branchId === "all" && (
        <Card>
          <CardContent className="p-4">
            <div className="font-medium mb-2">Top branches by revenue</div>
            <div className="h-72 w-full">
              <ResponsiveContainer>
                <BarChart data={perBranchBars}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }} />
                  <Legend />
                  <Bar dataKey="revenue" fill="hsl(var(--primary))" name="Revenue" />
                  <Bar dataKey="orders" fill="hsl(var(--muted-foreground))" name="Orders" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "danger" }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={cn("text-2xl font-semibold mt-1", tone === "danger" && "text-destructive")}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function DateField({ label, value, onChange }: { label: string; value?: Date; onChange: (d?: Date) => void }) {
  return (
    <div className="grid gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className={cn("w-[180px] justify-start text-left font-normal", !value && "text-muted-foreground")}>
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(value, "PP") : "Pick a date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={value} onSelect={onChange} initialFocus className={cn("p-3 pointer-events-auto")} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function resolveRange(preset: RangePreset, customFrom?: Date, customTo?: Date) {
  const now = new Date();
  if (preset === "custom") {
    return {
      from: startOfDay(customFrom ?? subDays(now, 30)),
      to: endOfDay(customTo ?? now),
    };
  }
  if (preset === "ytd") return { from: startOfDay(new Date(now.getFullYear(), 0, 1)), to: endOfDay(now) };
  const days = preset === "7d" ? 7 : preset === "30d" ? 30 : 90;
  return { from: startOfDay(subDays(now, days - 1)), to: endOfDay(now) };
}

function fmtMoney(cents: number) {
  return (cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
